import type { ApiDashboardElement } from '@highimpact-frontend/api-types';

import type { DashboardRuntimeAction } from '../state/dashboardRuntimeTypes';
import type { DashboardStoreType } from '../state/dashboard/initials';
import {
  findGridIdAndTypeByElementId,
  getDashboardAtKey,
} from './gridLookup';

export type DashboardRuntimeReducerAction =
  | DashboardRuntimeAction
  | { readonly type: 'replaceState'; readonly payload: DashboardStoreType };

const findWidgetKeyByElementId = (
  state: DashboardStoreType,
  elementId: number
): { gridType: 'grids' | 'reports'; gridId: number; widgetKey: string } | undefined => {
  const gridData = findGridIdAndTypeByElementId(elementId, state);
  if (!gridData) {
    return undefined;
  }

  const dashboard = getDashboardAtKey(gridData.id, gridData.type, state);
  if (!dashboard) {
    return undefined;
  }

  const widgetEntry = Object.entries(dashboard.widgets).find(
    ([, widget]) => widget.dashboardElementId === elementId
  );
  if (!widgetEntry) {
    return undefined;
  }

  return {
    gridType: gridData.type,
    gridId: gridData.id,
    widgetKey: widgetEntry[0],
  };
};

const updateWidgetElementData = (
  state: DashboardStoreType,
  elementId: number,
  element: ApiDashboardElement
): DashboardStoreType => {
  const location = findWidgetKeyByElementId(state, elementId);
  if (!location) {
    return state;
  }

  const dashboard = getDashboardAtKey(
    location.gridId,
    location.gridType,
    state
  );
  if (!dashboard) {
    return state;
  }

  const widget = dashboard.widgets[location.widgetKey];
  if (!widget) {
    return state;
  }

  return {
    ...state,
    [location.gridType]: {
      ...state[location.gridType],
      [location.gridId]: {
        ...dashboard,
        widgets: {
          ...dashboard.widgets,
          [location.widgetKey]: {
            ...widget,
            elementData: element,
          },
        },
      },
    },
  };
};

/** Pure reducer for in-library dashboard runtime state (Context + useReducer). */
export function dashboardRuntimeReducer(
  state: DashboardStoreType,
  action: DashboardRuntimeReducerAction
): DashboardStoreType {
  switch (action.type) {
    case 'hydrate':
    case 'replaceState':
      return action.payload;

    case 'updateElement':
      return updateWidgetElementData(state, action.elementId, action.element);

    case 'updateWidgetAdditionalProps': {
      const widget = readWidgetRecord(state, action.widgetId);
      if (!widget) {
        return state;
      }

      const { gridType, gridId, widgetKey, dashboard, entry } = widget;
      return {
        ...state,
        [gridType]: {
          ...state[gridType],
          [gridId]: {
            ...dashboard,
            widgets: {
              ...dashboard.widgets,
              [widgetKey]: {
                ...entry,
                props: {
                  ...entry.props,
                  ...action.additionalProps,
                },
              },
            },
          },
        },
      };
    }

    case 'setEditing':
      if (action.widgetId === null) {
        return {
          ...state,
          editing: { state: 'NotEditing' },
        };
      }

      {
        const widgetRecord = readWidgetRecord(state, action.widgetId);
        if (!widgetRecord) {
          return state;
        }

        return {
          ...state,
          editing: {
            state: 'EditingWithoutDisplayer',
            gridType: widgetRecord.gridType,
            gridId: widgetRecord.gridId,
            widgetId: action.widgetId,
          },
        };
      }

    case 'openConfigPanel': {
      const location = findWidgetKeyByElementId(state, action.elementId);
      if (!location) {
        return state;
      }

      const dashboard = getDashboardAtKey(
        location.gridId,
        location.gridType,
        state
      );
      const widget = dashboard?.widgets[location.widgetKey];
      if (!widget) {
        return state;
      }

      return {
        ...state,
        ConfigWidgetPanelState: {
          state: 'Configuring',
          gridId: location.gridId,
          gridType: location.gridType,
          widgetId: action.elementId,
          elementType: widget.elementData.element_type as Exclude<
            typeof widget.elementData.element_type,
            'collection' | 'readonlytext'
          >,
          specificWidgetType:
            widget.elementData.element_type === 'remotewidget'
              ? (widget.elementData as { properties?: { specific_widget_type?: string } })
                  .properties?.specific_widget_type
              : undefined,
        },
      };
    }

    case 'closeConfigPanel':
      return {
        ...state,
        ConfigWidgetPanelState: { state: 'NotConfiguring' },
      };

    default:
      return state;
  }
}

function readWidgetRecord(
  state: DashboardStoreType,
  widgetId: string
):
  | {
      gridType: 'grids' | 'reports';
      gridId: number;
      widgetKey: string;
      dashboard: NonNullable<ReturnType<typeof getDashboardAtKey>>;
      entry: NonNullable<
        ReturnType<typeof getDashboardAtKey>
      >['widgets'][string];
    }
  | undefined {
  for (const gridType of ['grids', 'reports'] as const) {
    for (const [gridId, dashboard] of Object.entries(state[gridType] ?? {})) {
      const entry = dashboard.widgets[widgetId];
      if (entry) {
        return {
          gridType,
          gridId: Number(gridId),
          widgetKey: widgetId,
          dashboard,
          entry,
        };
      }
    }
  }

  return undefined;
}
